<template>
	<RouterView v-slot="{ Component, route }">
		<!-- Browsing pages stay in memory so Back and tab switches return to the same scroll position. -->
		<KeepAlive :max="10">
			<component :is="Component" v-if="route.meta.keepAlive" :key="route.path" />
		</KeepAlive>
		<component :is="Component" v-if="!route.meta.keepAlive" :key="route.meta.usePathKey ? route.path : undefined" />
	</RouterView>
	<TabBar v-if="showTabBar" />
	<Toasts />
	<CartValidation :oos-items="oosItems" :low-stock-items="lowStockItems" :invalid-items="invalidItems" />
</template>

<script>
import TabBar from './components/TabBar.vue'
import Toasts from './components/Toasts.vue'
import CartValidation from './components/CartValidation.vue'
import { validateInventory } from '@front/helpers/validateCart.js'
import { useCheckoutStore } from '@front/stores/store'
import aa from 'search-insights'
import { openSheet } from './lib/sheets'
import { syncWishlists } from './lib/wishlists'

// A bad social login stored the member id as the string "undefined".
export function clearUndefinedMemberSession() {
	if (typeof window === 'undefined') return false
	let memberId = null
	try {
		memberId = window.localStorage.getItem('user')
	} catch {
		return false
	}
	if (memberId !== 'undefined') return false
	try {
		window.localStorage.removeItem('user')
		window.localStorage.removeItem('token')
		window.localStorage.removeItem('email')
	} catch {
		// storage can throw in private mode
	}
	if (typeof document !== 'undefined') {
		document.cookie = 'Authorization=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
	}
	return true
}

// main.js starts initUserCart as soon as this module has loaded. Drop the bad
// id before that fetch so boot does not call GET /members/undefined.
clearUndefinedMemberSession()

export default {
	components: { TabBar, Toasts, CartValidation },
	inject: ['store'],
	data() {
		return {
			oosItems: [],
			lowStockItems: [],
			invalidItems: [],
			checkout: useCheckoutStore(),
		}
	},
	computed: {
		showTabBar() {
			return this.$route.meta.tabBar !== false
		},
		loggedIn() {
			return this.store?.user?.id
		},
	},
	watch: {
		showTabBar: {
			immediate: true,
			handler(show) {
				document.documentElement.classList.toggle('no-tabbar', !show)
			},
		},
		loggedIn: {
			immediate: true,
			handler() {
				if (this.loggedIn) this.fetchWishlists()
			},
		},
		'store.cartInitialized': {
			immediate: true,
			handler() {
				if (this.store.cartInitialized && this.store.cart?.cartItems?.length) this.validateCartInventory()
			},
		},
	},
	mounted() {
		// Same session restore as the website (front/src/App.vue).
		const guest = JSON.parse(sessionStorage.getItem('guest')) || {}
		if (Object.keys(guest).length) this.checkout.guest = guest
		const addresses = JSON.parse(sessionStorage.getItem('addresses')) || []
		if (addresses.length) this.checkout.addresses = addresses
		// Guests' recently viewed products live on this device (the product page saves them).
		try {
			this.store.recentlyViewed = JSON.parse(window.localStorage.getItem('recentlyViewed')) || []
		} catch {
			this.store.recentlyViewed = []
		}

		clearUndefinedMemberSession()
		const memberId = window.localStorage.getItem('user')
		if (memberId && memberId !== 'undefined') aa('setAuthenticatedUserToken', memberId)
	},
	methods: {
		async fetchWishlists() {
			try {
				await syncWishlists(this.store)
			} catch {
				// keeps whatever lists were already loaded
			}
		},
		async validateCartInventory() {
			const { oosItems, lowStockItems, invalidItems } = await validateInventory()
			this.oosItems = oosItems
			this.lowStockItems = lowStockItems
			this.invalidItems = invalidItems
			if (oosItems.length || lowStockItems.length || invalidItems.length) openSheet('cart-updated')
		},
	},
}
</script>
